/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DataNode, DirectoryTreeProps } from 'antd/lib/tree';
import React, { Fragment, useState, useEffect, useContext } from 'react';
import { message, Tree } from 'antd';
import { useIntl } from 'react-intl';
import IconButton from '~src/components/_common/IconButton/IconButton';
import navigationItemsSu from '~src/data/navigationItemsSu';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useHistory, useLocation } from 'react-router-dom';
import { TREE_MIN_WIDTH } from '~src/config';
import { useProjectId } from '~src/hooks/useProjectId';
import { useProject } from '~src/hooks/api-hooks/useProject';
// import { useTemplates } from '~src/hooks/api-hooks/useTemplates';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { getParentsIds } from '~src/utils/tree';
import dataContextMenu from '~src/data/dataContextMenu';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import { DbFolder } from '~src/types/db';
import { useItemPermission } from '~src/hooks/useItemPermission';
import { downloadFolder } from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import { appLog } from '~src/utils/log';
import { useToggleFavouriteFolder } from '~src/hooks/actions/useToggleFavouriteFolder';
import { DeleteModal } from '~components/popups-modals/Delete.modal';
import { EditFolderModal } from '~components/popups-modals/EditFolder.modal';
import { FolderPropertiesModal } from '~components/popups-modals/FolderPropertiesModal/FolderPropertiesModal';
import { MoveModal } from '~components/popups-modals/Move.modal';
import { RightsModal } from '~components/popups-modals/RightsModal/RightsModal';
import { ItemLogModal } from '~components/popups-modals/ItemLog.modal';
import { DownloadingState, useStore } from '~src/store';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import doReturnTheme from '~src/styles/themeEngine';
import SidebarContext from '~src/contexts/SidebarContext';
import { arrToTree } from '../LeftSideView/utils';

const { DirectoryTree } = Tree;

// interface LeftSideViewSuProps {}
type StateDataNode = DataNode & {
  path?: string;
  children?: StateDataNode[];
  parentId?: string | null;
  favourite?: boolean;
};
export const LeftSideViewSu: React.FC<Record<string, unknown>> = () => {
  const styles = useStyles(styleFn);
  const { isSidebarMinimized } = useContext(SidebarContext);
  const { currentTheme } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);
  const intl = useIntl();
  const history = useHistory();
  const projectId = useProjectId();
  const location = useLocation();
  const projectQuery = useProject();
  // const templatesQuery = useTemplates();
  const foldersQuery = useFolders({ as_admin: true });
  const { setLargeFilesContext } = useContext(LargeFilesContext);
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { toggleFavouriteFolder } = useToggleFavouriteFolder();

  const [shareModal, setShareModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [folderEditModal, setFolderEditModal] = useState(false);
  const [folderMoveModal, setFolderMoveModal] = useState(false);
  const [folderPropertiesModal, setFolderPropertiesModal] = useState(false);
  const [folderRightsModal, setFolderRightsModal] = useState(false);
  const [itemLogModal, setItemLogModal] = useState(false);

  const [rightClickFolder, setRightClickFolder] = useState<DbFolder>();
  // const [rightClickFolder, setRightClickFolder] = useState<DbFolder>();
  const [
    rightClickFolderOverlay,
    setRightClickFolderOverlay,
  ] = useState<DbFolder>();
  const [treeData, setTreeData] = useState<StateDataNode[]>([
    {
      title: '',
      path: '/',
      key: 'home',
      icon: (
        <IconButton
          icon={['fad', 'house']}
          color="darkGray"
          customStyle={styles.icons}
          backgroundOff
        />
      ),
      parentId: null,
      children: navigationItemsSu.map((navItem) => ({
        title: intl.formatMessage({ id: navItem.nameTerm }),
        path: navItem.path,
        key: navItem.key,
        icon: (
          <IconButton
            icon={navItem.icon as IconProp}
            color={navItem.color}
            customStyle={styles.icons}
            backgroundOff
          />
        ),
        parentId: null,
      })),
    },
  ]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['home']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const rightClickFolderParentPermission = useItemPermission({
    folder: rightClickFolder,
  });

  const handleSelect: DirectoryTreeProps['onSelect'] = (keys, info) => {
    let path = '';
    // @ts-ignore-line
    const nodePath: string | undefined = info.node.path;
    if (nodePath) {
      // if nodePath='/', navigate to ''
      path = nodePath?.length > 1 ? nodePath : '';
    } else if (info.node.pos.split('-')[2] === '1') {
      path = `/templates/${String(info.node.key).split('-')[1]}`;
    } else if (info.node.pos.split('-')[2] === '0') {
      path = `/archive/${info.node.key}`;
    }
    // setSelectedKeys is called in location change effect, triggered by history.push
    history.push(`/projects/${projectId}` + path);
  };

  useEffect(() => {
    if (projectQuery.data) {
      setTreeData((prevState) => {
        const nextState = [...prevState];
        nextState[0].title =
          projectQuery.data?.name || intl.formatMessage({ id: 'home' });
        return nextState;
      });
    }
  }, [projectQuery.data]);

  useEffect(() => {
    if (foldersQuery.data) {
      const archiveChildren = arrToTree(foldersQuery.data, null, theme);

      setTreeData((prevTreeData) => {
        const nextTreeData = [...prevTreeData];
        if (nextTreeData[0].children) {
          // archive is first child of home
          nextTreeData[0].children[0].children = archiveChildren;
        }

        return nextTreeData;
      });
    }
  }, [foldersQuery.data]);

  // Templates tree population hidden
  // useEffect(() => {
  //   if (templatesQuery.data) {
  //     setTreeData((prevTreeData) => {
  //       const nextTreeData = [...prevTreeData];
  //       if (nextTreeData[0].children && templatesQuery.data) {
  //         nextTreeData[0].children[1].children = templatesQuery.data.map(
  //           (item, index) => ({
  //             key: `template-${item.id}`,
  //             title: item.name,
  //             createdAt: item.start_date,
  //             icon: <TemplateIcon />,
  //           })
  //         );
  //       }
  //       return nextTreeData as StateDataNode[];
  //     });
  //   }
  // }, [templatesQuery.data]);

  useEffect(() => {
    const locationList = location.pathname.split('/').filter(Boolean);

    if (locationList.length > 3) {
      // Templates navigation hidden
      // if (locationList[2] === 'templates' && templatesQuery.data) {
      //   const templateId = locationList[3];
      //   setExpandedKeys((prevExpandedKeys) => {
      //     if (prevExpandedKeys.includes(templateId)) {
      //       return [...prevExpandedKeys];
      //     }
      //     return [...prevExpandedKeys, 'templates', templateId];
      //   });
      //   setSelectedKeys((prevSelectedKeys) =>
      //     prevSelectedKeys[0] === templateId
      //       ? [...prevSelectedKeys]
      //       : [templateId]
      //   );
      //   return;
      // } else
      if (locationList[2] === 'archive' && foldersQuery.data) {
        const folderId = locationList[3];

        setExpandedKeys((prevExpandedKeys) => {
          if (prevExpandedKeys.includes(folderId)) {
            return [...prevExpandedKeys];
          }

          const parents = getParentsIds(foldersQuery.data || [], folderId);

          return [...prevExpandedKeys, 'archive', ...parents, folderId];
        });

        setSelectedKeys((prevSelectedKeys) =>
          prevSelectedKeys[0] === folderId ? [...prevSelectedKeys] : [folderId]
        );
        return;
      }
    }

    if (locationList.length > 1) {
      const matchingItem = locationList[2]
        ? [...treeData, ...(treeData[0].children as StateDataNode[])].find(
            (v) => v.path === '/' + locationList[2]
          )
        : treeData[0];

      if (matchingItem) {
        setExpandedKeys((prevExpandedKeys) =>
          prevExpandedKeys.includes(matchingItem.key as string)
            ? [...prevExpandedKeys]
            : [...prevExpandedKeys, matchingItem.key as string]
        );

        setSelectedKeys((prevSelectedKeys) =>
          prevSelectedKeys[0] === matchingItem.key
            ? [...prevSelectedKeys]
            : [matchingItem.key as string]
        );
      }
    }
  }, [location.pathname, treeData, foldersQuery.data]);

  const handleRightClick: DirectoryTreeProps['onRightClick'] = ({
    node,
    event,
  }) => {
    if (navigationItemsSu.some((item) => item.key === node.key)) {
      setRightClickFolder(undefined);
      setRightClickFolderOverlay(undefined);
    }
    const found = foldersQuery.data?.find(
      (folder) => String(folder.id) === node.key
    );

    setRightClickFolder(found ? { ...found } : undefined);
    setRightClickFolderOverlay(found ? { ...found } : undefined);
  };

  const toggleFavourite = async () => {
    if (!rightClickFolder) return;
    setTimeout(() => toggleFavouriteFolder(rightClickFolder), 100);
  };

  const { changeDownloadingActive, changeDownloadingState } = useStore();

  const rightClickAction = (modal: string) => {
    const modalsMap = {
      share: setShareModal,
      'folder-edit': setFolderEditModal,
      'folder-move': setFolderMoveModal,
      'folder-properties': setFolderPropertiesModal,
      'rights-managment': setFolderRightsModal,
      'item-log': setItemLogModal,
      delete: setDeleteModal,
    };

    // set modal visible
    if (modalsMap[modal]) {
      modalsMap[modal](true);
    } else {
      // non-modal actions
      if (modal === 'folder-download') {
        if (!rightClickFolder) return;

        // activate download progress panel
        changeDownloadingActive(true);
        changeDownloadingState(DownloadingState.PREPARING);

        // const hidePrepMsg = message.loading(
        //   intl.formatMessage({ id: 'download-preparing' }),
        //   0
        // );
        return downloadFolder({
          folder: rightClickFolder,
          allFolders: foldersQuery.data || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            // hidePrepMsg();
            setTimeout(
              () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
              400
            );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          // hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (modal === 'favourite') {
        toggleFavourite();
      }
    }
  };

  return (
    <Fragment>
      <div css={styles.container} onContextMenu={(e) => e.preventDefault}>
        <RightClickOverlay
          leftSide={true}
          permissionLevel={rightClickFolderParentPermission}
          items={dataContextMenu.folder}
          onItemClick={(item) => {
            rightClickAction(item.modal);
            setTimeout(() => setRightClickFolderOverlay(undefined), 1000);
          }}
          onOverlayClose={() => {
            if (rightClickFolder)
              setTimeout(() => setRightClickFolderOverlay(undefined), 1000);
          }}
          selected={{ folder: rightClickFolderOverlay }}
          disabled={!rightClickFolder}>
          <DirectoryTree
            className="draggable-tree su-template-tree"
            blockNode
            treeData={treeData}
            selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            onSelect={handleSelect}
            onExpand={(keys) => setExpandedKeys(keys as string[])}
            onRightClick={handleRightClick}
            css={{
              width: ['30vw', 'max-content'],
              minWidth: [
                `${TREE_MIN_WIDTH - 10}px`,
                `max(${TREE_MIN_WIDTH - 10}px, 18.9vw)`,
              ],
            }}
            titleRender={(node: StateDataNode) => node.title}
          />
        </RightClickOverlay>
      </div>
      <DeleteModal
        visible={deleteModal}
        folders={rightClickFolder ? [rightClickFolder] : undefined}
        onCancel={() => setDeleteModal(false)}
      />
      <EditFolderModal
        visible={folderEditModal}
        folder={rightClickFolder}
        onCancel={() => setFolderEditModal(false)}
      />
      <MoveModal
        visible={folderMoveModal}
        folders={rightClickFolder ? [rightClickFolder] : undefined}
        folderId={rightClickFolder?.parent_id || null}
        onCancel={() => setFolderMoveModal(false)}
      />
      <RightsModal
        visible={folderRightsModal}
        onCancel={() => setFolderRightsModal(false)}
        folder={rightClickFolder}
      />
      <FolderPropertiesModal
        visible={folderPropertiesModal}
        onCancel={() => setFolderPropertiesModal(false)}
        folder={rightClickFolder}
      />
      <ItemLogModal
        visible={itemLogModal}
        onCancel={() => {
          setItemLogModal(false);
          setRightClickFolderOverlay(undefined);
        }}
        folder={rightClickFolder}
      />
    </Fragment>
  );
};

const TemplateIcon = (props) => {
  const styles = useStyles(styleFn);

  return (
    <IconButton
      iconStyle="fad"
      icon="folder-tree"
      color="yellow"
      customStyle={styles.icons}
      backgroundOff
      {...props}
    />
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    height: '100%',
    maxHeight: '100%',
    width: '100%',
    padding: 5,
    overflow: 'auto',
    backgroundColor: theme.colors.primaryLight,
  },
  icons: {
    fontSize: '20px',
    transition: '0.3s',
    margin: '3px 0 10px -16px',
  },
  badge: {
    right: '-4px',
    bottom: '14px',
  },
});
